import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../../core/config/firebase';
import { getEasternDate } from '../../wordle/utils/dailyWordUtils';

const COLLECTION_NAME = 'campusGuesserResults';

/**
 * Add rankings with tie handling to leaderboard data
 * Ties are determined by same score AND same time
 */
function addRankings(data) {
  const rankedData = [];
  let currentRank = 1;
  let prevScore = null;
  let prevTime = null;

  for (let i = 0; i < data.length; i++) {
    const entry = data[i];
    const isTied = prevScore === entry.score && prevTime === entry.completionTimeMs;

    if (!isTied && i > 0) {
      currentRank = i + 1;
    }

    rankedData.push({
      ...entry,
      rank: currentRank,
      isTied
    });

    prevScore = entry.score;
    prevTime = entry.completionTimeMs;
  }

  return rankedData;
}

/**
 * Get today's Campus Guesser leaderboard
 * Ranked by: 1) Highest score, 2) Fastest time (tiebreaker)
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @param {number} limit - Maximum number of entries
 * @returns {Promise<Array>} - Ranked leaderboard entries
 */
export async function getDailyGuesserLeaderboard(dateString, limit = 50) {
  try {
    const targetDate = dateString || getEasternDate();

    // Query for today's results
    const q = query(
      collection(db, COLLECTION_NAME),
      where('date', '==', targetDate)
    );

    const querySnapshot = await getDocs(q);
    const results = [];

    // Map results - only include entries with username (logged in users)
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.username) {
        results.push({
          id: docSnap.id,
          username: data.username,
          score: data.score,
          distance: data.distance,
          completionTimeMs: data.completionTimeMs,
          timestamp: data.timestamp,
          date: data.date
        });
      }
    });

    // Sort: highest score first, then fastest time
    results.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score; // Higher score is better
      }
      return a.completionTimeMs - b.completionTimeMs; // Faster time is better
    });

    // Limit and add rankings
    const limitedResults = results.slice(0, limit);
    return addRankings(limitedResults);
  } catch (error) {
    console.error('Error fetching daily guesser leaderboard:', error);
    throw error;
  }
}

/**
 * Get this week's Campus Guesser leaderboard
 * Shows best score per user (one appearance per player)
 * @param {number} limit - Maximum number of entries
 * @returns {Promise<Array>} - Ranked leaderboard entries
 */
export async function getWeeklyGuesserLeaderboard(limit = 50) {
  try {
    // Calculate start of week (Monday in Eastern Time)
    const now = new Date();
    const easternNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const dayOfWeek = easternNow.getDay();
    const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

    const mondayDate = new Date(easternNow);
    mondayDate.setDate(easternNow.getDate() - daysToMonday);
    mondayDate.setHours(0, 0, 0, 0);

    const mondayTimestamp = Timestamp.fromDate(mondayDate);

    // Query for this week's results
    const q = query(
      collection(db, COLLECTION_NAME),
      where('timestamp', '>=', mondayTimestamp)
    );

    const querySnapshot = await getDocs(q);
    const userBestScores = new Map(); // userId -> best score entry

    // Keep only the best score per user
    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (!data.username) return; // Skip entries without username

      const userId = data.userId;
      const existingBest = userBestScores.get(userId);

      // Check if this is better than user's current best
      if (!existingBest ||
          data.score > existingBest.score ||
          (data.score === existingBest.score && data.completionTimeMs < existingBest.completionTimeMs)) {
        userBestScores.set(userId, {
          id: docSnap.id,
          userId,
          username: data.username,
          score: data.score,
          distance: data.distance,
          completionTimeMs: data.completionTimeMs,
          timestamp: data.timestamp,
          date: data.date
        });
      }
    });

    // Convert map to array
    const results = Array.from(userBestScores.values());

    // Sort: highest score first, then fastest time
    results.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      return a.completionTimeMs - b.completionTimeMs;
    });

    // Limit and add rankings
    const limitedResults = results.slice(0, limit);
    return addRankings(limitedResults);
  } catch (error) {
    console.error('Error fetching weekly guesser leaderboard:', error);
    throw error;
  }
}

/**
 * Get all-time best individual scores
 * Players can appear multiple times
 * @param {number} limit - Maximum number of entries
 * @returns {Promise<Array>} - Ranked leaderboard entries
 */
export async function getBestGuesserScores(limit = 50) {
  try {
    // Query all results, we'll sort client-side
    // Note: Firestore doesn't support ordering by multiple fields without composite index
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy('score', 'desc'),
      firestoreLimit(limit * 2) // Get extra to account for entries without username
    );

    const querySnapshot = await getDocs(q);
    const results = [];

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.username) {
        results.push({
          id: docSnap.id,
          username: data.username,
          score: data.score,
          distance: data.distance,
          completionTimeMs: data.completionTimeMs,
          timestamp: data.timestamp,
          date: data.date
        });
      }
    });

    // Sort: highest score first, then fastest time
    results.sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }
      return a.completionTimeMs - b.completionTimeMs;
    });

    // Limit and add rankings
    const limitedResults = results.slice(0, limit);
    return addRankings(limitedResults);
  } catch (error) {
    console.error('Error fetching best guesser scores:', error);
    throw error;
  }
}

/**
 * Get all-time best players by average score
 * Ranked by average score (higher is better)
 * @param {number} limit - Maximum number of entries
 * @param {number} minGames - Minimum games required to appear
 * @returns {Promise<Array>} - Ranked player stats
 */
export async function getBestGuesserPlayers(limit = 50, minGames = 1) {
  try {
    // Get all results and aggregate by user
    const q = query(collection(db, COLLECTION_NAME));
    const querySnapshot = await getDocs(q);

    const playerStats = new Map(); // userId -> stats

    querySnapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (!data.username || !data.userId) return;

      const userId = data.userId;
      const existing = playerStats.get(userId);

      if (existing) {
        existing.totalScore += data.score;
        existing.totalDistance += data.distance;
        existing.gamesPlayed += 1;
        existing.bestScore = Math.max(existing.bestScore, data.score);
      } else {
        playerStats.set(userId, {
          userId,
          username: data.username,
          totalScore: data.score,
          totalDistance: data.distance,
          gamesPlayed: 1,
          bestScore: data.score
        });
      }
    });

    // Convert to array and calculate averages
    const results = [];
    playerStats.forEach((stats) => {
      if (stats.gamesPlayed >= minGames) {
        results.push({
          username: stats.username,
          avgScore: Math.round(stats.totalScore / stats.gamesPlayed),
          avgDistance: Math.round(stats.totalDistance / stats.gamesPlayed),
          gamesPlayed: stats.gamesPlayed,
          bestScore: stats.bestScore
        });
      }
    });

    // Sort by average score (higher is better)
    results.sort((a, b) => {
      if (b.avgScore !== a.avgScore) {
        return b.avgScore - a.avgScore;
      }
      return b.gamesPlayed - a.gamesPlayed; // Tiebreaker: more games played
    });

    // Limit and add rankings
    const limitedResults = results.slice(0, limit);
    
    // Add rankings for best players view
    const rankedResults = [];
    let currentRank = 1;
    let prevAvg = null;

    for (let i = 0; i < limitedResults.length; i++) {
      const entry = limitedResults[i];
      const isTied = prevAvg === entry.avgScore;

      if (!isTied && i > 0) {
        currentRank = i + 1;
      }

      rankedResults.push({
        ...entry,
        rank: currentRank,
        isTied
      });

      prevAvg = entry.avgScore;
    }

    return rankedResults;
  } catch (error) {
    console.error('Error fetching best guesser players:', error);
    throw error;
  }
}

